/**
 * Everything a partner needs to actually sell Dunea: what the product is, what
 * it costs, what a referral is worth to them, and the rules of the program.
 *
 * Plan prices and credits come from the server's plan list (the same one the
 * Billing screen sells from), so a partner never quotes a number Dunea doesn't charge.
 */
import { useQuery } from '@tanstack/react-query';
import { classNames } from '~/utils/classNames';
import { ACCOUNT_PLANS_KEY, apiGetAccountPlans } from '~/lib/api/plans';
import { formatCredits } from '~/lib/plans-model';
import { CARD, PageHead, Panel, usePortal } from './shared';

const PITCH = [
  {
    icon: 'i-ph:lightning',
    title: 'A site from a sentence',
    body: 'Describe the site you want and Dunea generates and deploys a real, full-stack one — not a template with your logo dropped in.',
  },
  {
    icon: 'i-ph:users-three',
    title: 'Built for non-developers',
    body: 'Founders, freelancers, and anyone who needs a professional web presence without hiring an agency or learning to code.',
  },
  {
    icon: 'i-ph:globe-simple',
    title: 'Live in minutes',
    body: 'Hosting, deploys, and custom domains are handled in-product. There is no separate host to configure.',
  },
];

const AUDIENCES = [
  'Creators teaching people to build or launch online',
  'Newsletters for founders, indie hackers, or designers',
  'Agencies and freelancers who spin up client sites',
  'Communities where people ask “what should I build my site with?”',
];

const RULES = [
  ['What earns', 'Subscriptions and credit purchases made by anyone who signed up through your link.'],
  ['What does not', 'Domain purchases — those resell near cost, so there is no margin to share.'],
  ['How long', 'Six months from that customer’s signup date. Every renewal inside the window earns.'],
  [
    'Attribution',
    'First touch, and permanent. If they visit your link and sign up, they are yours — later links cannot take them.',
  ],
  [
    'Not allowed',
    'Referring yourself, paid search on Dunea’s brand terms, or signup farming. Accounts sharing an IP get flagged automatically.',
  ],
];

export default function Resources() {
  const { data } = usePortal();
  const rateBps = data.affiliate!.commissionRateBps;
  const months = data.affiliate!.windowMonths;
  const cut = rateBps / 10000;
  const plansQuery = useQuery({ queryKey: ACCOUNT_PLANS_KEY, queryFn: apiGetAccountPlans, staleTime: 60 * 60 * 1000 });

  return (
    <div className="flex flex-col gap-8">
      <PageHead
        title="Resources"
        subtitle="What Dunea is, who it lands with, and what each plan is worth to you. Everything here is safe to repeat publicly."
      />

      {/* What Dunea is */}
      <section>
        <h2 className="ui-caption mb-3 text-muted">What you’re promoting</h2>
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline sm:grid-cols-3">
          {PITCH.map((p) => (
            <div key={p.title} className="bg-surface px-5 py-5">
              <span className={classNames(p.icon, 'text-[18px] text-accent')} />
              <h3 className="mt-3 text-[13px] font-medium text-primary">{p.title}</h3>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Plans + what each is worth */}
      <section>
        <h2 className="ui-caption mb-3 text-muted">Plans, and what each referral pays you</h2>
        <div className={classNames(CARD, 'overflow-hidden')}>
          <div className="hidden grid-cols-[1fr_auto_auto_auto] gap-4 border-b border-hairline px-5 py-2.5 sm:grid">
            <span className="ui-caption text-faint">Plan</span>
            <span className="ui-caption w-24 text-right text-faint">Price</span>
            <span className="ui-caption w-24 text-right text-faint">You get</span>
            <span className="ui-caption w-28 text-right text-faint">Over {months} months</span>
          </div>

          {plansQuery.isLoading ? (
            <div className="px-5 py-6 text-center text-[12.5px] text-muted">Loading plans…</div>
          ) : plansQuery.isError || !plansQuery.data ? (
            <div className="px-5 py-6 text-center">
              <div className="mb-2 text-[12.5px] text-primary">Couldn't load plans</div>
              <button type="button" onClick={() => plansQuery.refetch()} className="ui-btn-secondary">
                Retry
              </button>
            </div>
          ) : (
            <div className="divide-y divide-hairline">
              {plansQuery.data.plans.map((plan) => {
                const perMonth = plan.priceUsd * cut;
                const total = perMonth * months;

                return (
                  <div
                    key={plan.name}
                    className="grid grid-cols-1 gap-1 px-5 py-3.5 sm:grid-cols-[1fr_auto_auto_auto] sm:gap-4"
                  >
                    <div>
                      <div className="text-[13px] text-primary">{plan.name}</div>
                      <div className="mt-0.5 text-[11.5px] text-muted">
                        {plan.tier === 'free'
                          ? `${formatCredits(plan.credits)} credits, once`
                          : `${formatCredits(plan.credits)} credits a month`}
                      </div>
                    </div>
                    {/* Inline labels stand in for the header row that phones don't show. */}
                    <div className="text-[13px] tabular-nums text-secondary sm:w-24 sm:text-right">
                      <span className="text-muted sm:hidden">Price </span>
                      {plan.priceUsd === 0 ? 'Free' : `$${plan.priceUsd}/mo`}
                    </div>
                    <div className="text-[13px] tabular-nums text-secondary sm:w-24 sm:text-right">
                      <span className="text-muted sm:hidden">You get </span>
                      {perMonth > 0 ? `$${perMonth.toFixed(2)}` : '—'}
                    </div>
                    <div
                      className={classNames(
                        'text-[13px] font-medium tabular-nums sm:w-28 sm:text-right',
                        total > 0 ? 'text-accent' : 'text-faint',
                      )}
                    >
                      <span className="font-normal text-muted sm:hidden">Over {months} months </span>
                      {total > 0 ? `$${total.toFixed(2)}` : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t border-hairline bg-raised/40 px-5 py-3">
            <p className="text-[11.5px] leading-relaxed text-muted">
              Assumes the customer stays on the plan for the full window. Credit top-ups they buy along the way earn you{' '}
              {rateBps / 100}% as well. Annual plans are billed for ten months and credited for twelve — you earn on
              what they actually pay.
            </p>
          </div>
        </div>
      </section>

      {/* Who it lands with */}
      <Panel title="Audiences it lands with">
        <ul className="divide-y divide-hairline">
          {AUDIENCES.map((audience) => (
            <li key={audience} className="flex items-start gap-2.5 px-5 py-3">
              <span className="i-ph:check mt-0.5 shrink-0 text-[13px] text-accent" />
              <span className="text-[12.5px] leading-relaxed text-secondary">{audience}</span>
            </li>
          ))}
        </ul>
      </Panel>

      {/* Program rules */}
      <Panel title="Program rules">
        <dl className="divide-y divide-hairline">
          {RULES.map(([term, description]) => (
            <div key={term} className="grid grid-cols-1 gap-1 px-5 py-3.5 sm:grid-cols-[160px_1fr] sm:gap-4">
              <dt className="text-[12.5px] text-primary">{term}</dt>
              <dd className="text-[12.5px] leading-relaxed text-muted">{description}</dd>
            </div>
          ))}
        </dl>
      </Panel>

      <div className="ui-banner-info">
        <span className="i-ph:envelope-simple shrink-0 text-[14px]" />
        <span>
          Need a custom rate, co-marketing, or assets for a launch? Reply to your approval email and we’ll sort it out.
        </span>
      </div>
    </div>
  );
}
